import React, { useRef, useState, useMemo, useContext } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import DataGrid from "../DataGrid/index.js";
import Toggle from "../DataGrid/toggle.js";
import { Context } from "../../Reducers/Store";

/**
 * Componente base para listagens
 * 
 * @param {Object} props
 * @param {string} props.titulo - Título da página
 * @param {string} props.subtitulo - Subtítulo da página (opcional)
 * @param {string} props.endpoint - URL da API (sem REACT_APP_API)
 * @param {string} props.baseRoute - Rota base para navegação (ex: "/controle-usuarios")
 * @param {Function} props.getColumns - Função que retorna as colunas (recebe { podeEditar, navigate, onToggleStatus })
 * @param {Object} props.permissoes - Códigos das permissões { criar, editar, remover }
 * @param {string} props.emptyMessage - Mensagem quando não há registros
 * @param {string} props.novoButtonText - Texto do botão de novo registro
 * @param {string} props.emptyButtonText - Texto do botão quando lista vazia
 * @param {boolean} props.showStatusFilter - Exibir filtro de ativo/inativo (default: true)
 * @param {boolean} props.showActions - Exibir botões de criar/editar (default: true)
 * @param {boolean} props.allowRowClick - Permitir clique na linha para editar (default: true)
 * @param {string} props.toggleEndpoint - Endpoint específico para toggle de status (opcional)
 * @param {Function} props.onToggleStatus - Função customizada para toggle (opcional)
 * @param {Object} props.extraFilters - Filtros adicionais fixos
 */
export default function BaseList({
  titulo,
  subtitulo,
  endpoint,
  baseRoute,
  getColumns,
  permissoes = {},
  emptyMessage = "Nenhum registro encontrado",
  novoButtonText = "Novo",
  emptyButtonText = "Cadastrar primeiro registro",
  showStatusFilter = true,
  showActions = true,
  allowRowClick = true,
  toggleEndpoint,
  onToggleStatus,
  extraFilters = {},
}) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const gridRef = useRef();
  const [state] = useContext(Context);

  const filtroInicial = searchParams.get("filtro") || "";
  const [ativoFiltro, setAtivoFiltro] = useState(searchParams.get("ativo") ?? "true");

  // Helper para verificar permissão
  const temPermissao = (codigo) => {
    if (!codigo) return true;
    return state?.profile?.permissoes?.includes(codigo) ?? false;
  };

  const podeCriar = temPermissao(permissoes.criar);
  const podeEditar = temPermissao(permissoes.editar);

  const handleToggleStatus = async (id, ativo) => {
    if (!podeEditar) return;

    try {
      if (onToggleStatus) {
        await onToggleStatus(id, ativo);
      } else {
        const toggleUrl = toggleEndpoint || endpoint;
        await axios.put(`${process.env.REACT_APP_API}/${toggleUrl}/${id}`, {
          ativo: !ativo
        });
      }
      gridRef.current?.refresh();
    } catch (error) {
      console.error("Erro ao alterar status:", error);
    }
  };

  const handleAtivoChange = (valor) => {
    setAtivoFiltro(valor);
    const params = new URLSearchParams(searchParams);
    if (valor !== "") {
      params.set("ativo", valor);
    } else {
      params.delete("ativo");
    }
    setSearchParams(params);
  };

  // Monta as colunas chamando a função passada por props
  const columns = useMemo(() => {
    if (typeof getColumns !== 'function') {
      console.error('BaseList: getColumns deve ser uma função');
      return [];
    }
    return getColumns({
      podeCriar,
      podeEditar,
      navigate,
      baseRoute,
      onToggleStatus: handleToggleStatus,
      temPermissao,
    });
  }, [podeCriar, podeEditar, navigate, baseRoute, temPermissao]);

  const externalFilters = useMemo(() => {
    const filters = { ...extraFilters };
    if (showStatusFilter && ativoFiltro !== "") {
      filters.ativo = ativoFiltro;
    }
    return filters;
  }, [ativoFiltro, extraFilters, showStatusFilter]);

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div>
          <h1 className="text-xl font-bold">{titulo}</h1>
          {subtitulo && <p className="text-sm text-gray-500">{subtitulo}</p>}
          {showStatusFilter && ativoFiltro !== "true" && (
            <button
              onClick={() => {
                setAtivoFiltro("true");
                navigate(`${baseRoute}?ativo=true`);
              }}
              className="text-sm text-blue-600 hover:underline"
            >
              Limpar filtros
            </button>
          )}
        </div>

        <div className="flex items-center gap-4">
          {showStatusFilter && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Status:</span>
              <select
                value={ativoFiltro}
                onChange={(e) => handleAtivoChange(e.target.value)}
                className="border rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-300"
              >
                <option value="true">Ativos</option>
                <option value="false">Inativos</option>
                <option value="">Todos</option>
              </select>
            </div>
          )}
        </div>
      </div>

      <DataGrid
        ref={gridRef}
        endpoint={`${process.env.REACT_APP_API}/${endpoint}`}
        columns={columns}
        filters={externalFilters}
        initialFilter={filtroInicial}
        emptyMessage={emptyMessage}
        emptyAction={
          showActions && podeCriar ? (
            <button
              onClick={() => navigate(`${baseRoute}/novo`)}
              className="px-4 py-2 text-sm rounded bg-slate-900 text-white hover:bg-slate-800"
            >
              {emptyButtonText}
            </button>
          ) : null
        }
        headerAction={
          showActions && podeCriar ? (
            <button
              onClick={() => navigate(`${baseRoute}/novo`)}
              className="px-4 py-2 text-sm rounded bg-slate-900 text-white hover:bg-slate-800"
            >
              {novoButtonText}
            </button>
          ) : null
        }
        onRowClick={showActions && allowRowClick && podeEditar ? (row) => navigate(`${baseRoute}/${row.id}`) : undefined}
      />
    </div>
  );
}